import { ROUTES } from "../../consts";
import { useTrackContext } from "../../hooks/useTrackContext";
import { ProductSelection } from "../common/ProductSectionAndDate";
import Notification from "../Notification";
import { useEffect, useMemo, useState } from "react";
import ProgressBar from "../ProgressiveBar";
import FAQModalAccordion from "../ModalAccordion/FAQModalAccordion";
import { IGVariant, TTermsAndConditionsContent } from "../../types";
import NewsletterPopup from "../NewsletterPopup";
import ModalWithAccordion from "../ModalAccordion";
import { SHOPIFY_STOREFRONT_GRAPHQL_API, SHOPIFY_STOREFRONT_TOKEN } from "../../config";
import { GET_METAOBJECT_BY_HANDLE } from "../../graphqlQueries";
import BottomSummary from "../BottomSummary";

const JuniorTrack = ({
  selectProductOnly,
  onVariantSelection,
}: {
  selectProductOnly?: boolean;
  onVariantSelection?: (isAdded: boolean, variant: IGVariant, selectedVariants: IGVariant[]) => void;
}) => {
  const { juniorTrackSelection, windowWidth, productRenderedLocation, appHostedAt, termsAndConditionsContent } = useTrackContext();
  const [isActive, setIsActive] = useState<boolean>(true);
  const [nextRoute, setNextRoute] = useState<string>("/" + ROUTES.JUNIOR_TRACK.ReviewCart);
  const [juniorTrackFAQS, setJuniorTrackFAQS] = useState<TTermsAndConditionsContent[]>([]);

  useEffect(() => {
    if (juniorTrackSelection.variantsAndDate.selectedVariants.length === juniorTrackSelection.racesSeats.numberOfRaces) {
      setIsActive(true);
      setNextRoute("/" + ROUTES.JUNIOR_TRACK.ReviewCart);
    } else {
      setIsActive(false);
      setNextRoute("#");
    }
  }, [juniorTrackSelection]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(SHOPIFY_STOREFRONT_GRAPHQL_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
          },
          body: JSON.stringify({
            query: GET_METAOBJECT_BY_HANDLE,
            variables: {
              handle: "junior_track_faqs",
              type: "information_react_component",
            },
          }),
        });

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const data = await response.json();
        const fields = data.data.metaobject.fields;
        const [value] = fields;
        setJuniorTrackFAQS(value.value);
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, []);

  const numberOfSelectedVariants: number = useMemo(() => {
    return juniorTrackSelection.variantsAndDate.selectedVariants.length;
  }, [juniorTrackSelection.variantsAndDate.selectedVariants]);

  const numberOfRaces: number = useMemo(() => {
    return juniorTrackSelection.racesSeats.numberOfRaces;
  }, [juniorTrackSelection.racesSeats.numberOfRaces]);

  if (selectProductOnly) {
    return <ProductSelection selectProductOnly={selectProductOnly} onVariantSelection={onVariantSelection} />;
  }

  return (
    <>
      <div className="tw-relative">
        <Notification />
      </div>
      <ProgressBar maxValue={100} currentValue={windowWidth <= 768 ? 73 : 68} />

      <ProductSelection />
      <div
        className={`${productRenderedLocation === "external" ? "tw-container" : "container"} tw-px-[10px] xl:tw-px-[0] tw-mx-auto tw-grid ${
          appHostedAt && appHostedAt === "bosshunting" ? "tw-grid-cols-1" : "tw-grid-cols-1 xl:tw-grid-cols-2"
        } tw-mt-[20px]`}
      >
        <div className="tw-invisible" />
        <div className={`tw-flex tw-justify-center ${productRenderedLocation === "external" ? "tw-container" : "container"}`}>
          {juniorTrackFAQS && juniorTrackFAQS.length && <FAQModalAccordion title={"Help with your booking"} sections={JSON.parse(`${juniorTrackFAQS}`)} />}
        </div>
      </div>

      <div className="tw-w-full lg:tw-h-[126px] tw-p-[30px] tw-mt-[40px] lg:tw-mt-0">
        <div className="tw-flex tw-justify-center tw-items-center">
          <div
            className={`${
              productRenderedLocation === "external" ? "tw-container tw-mx-auto" : "container mx-auto"
            } tw-relative tw-flex tw-justify-end tw-items-center tw-gap-[15px] lg:tw-block`}
          >
            <NewsletterPopup />
            {termsAndConditionsContent && termsAndConditionsContent.length && (
              <ModalWithAccordion trackTitle="JuniorTrack" title={"Terms & Conditions"} sections={JSON.parse(`${termsAndConditionsContent}`)} />
            )}
          </div>
        </div>
      </div>

      <BottomSummary
        previousInfo={{ pageName: "Seats and Races", pagePath: "/" + ROUTES.JUNIOR_TRACK.SEATS_RACES }}
        nextInfo={{
          pageName: `Review (${numberOfSelectedVariants}/${numberOfRaces})`,
          pagePath: nextRoute,
          isActive: isActive,
          component:
            (juniorTrackFAQS && juniorTrackFAQS.length && <FAQModalAccordion showFAQIcon={true} title={"Help with your booking"} sections={JSON.parse(`${juniorTrackFAQS}`)} />) ||
            undefined,
        }}
      />
    </>
  );
};

export default JuniorTrack;
